import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Loader2,
  Download,
  X,
  CheckCircle2,
  AlertTriangle,
  Target,
  ListChecks,
  ChevronDown,
} from "lucide-react";
import { formatDateTime, formatLKR, formatPct } from "@/lib/api";
import { VisionOcrWarning } from "@/components/legal/DataNotes";

/**
 * ReportAnalysisModal — Phase 6 / Fix 1
 *
 * Renders Claude's structured 5-part report analysis with:
 *   1. Fixed header (title + meta + key-metrics chips)
 *   2. Scrollable body with five colour-coded sections
 *   3. Faded "Scroll for more" indicator that disappears at the bottom
 *
 * Backwards-compatible with the older 2-part analysis: falls back to
 * regex-splitting the raw narrative when structured fields are absent.
 */

const stripBoldMarkdown = (s) =>
  (s || "").replace(/\*\*(.*?)\*\*/g, "$1").replace(/\*(.*?)\*/g, "$1");

const splitSections = (raw) => {
  if (!raw) return {};
  const text = String(raw);
  // Match "## PART 1", "PART 1 —", "PART 1 -" headings tolerantly.
  const find = (n, fallbackStart = false) => {
    const re = new RegExp(`(?:^|\\n)\\s*(?:#{1,3}\\s*)?PART\\s*${n}\\b[^\\n]*`, "i");
    const m = text.match(re);
    return m ? m.index + m[0].length : (fallbackStart ? 0 : -1);
  };
  const idx1 = find(1, true);
  const idx2 = find(2);
  const idx3 = find(3);
  const idx4 = find(4);
  const idx5 = find(5);
  const ends = [idx2, idx3, idx4, idx5, text.length].filter((x) => x >= 0);
  const slice = (start, nextCandidates) => {
    if (start < 0) return "";
    const next = nextCandidates.filter((x) => x > start);
    const end = next.length ? Math.min(...next) : text.length;
    return text.slice(start, end).trim();
  };
  return {
    narrative: slice(idx1, [idx2, idx3, idx4, idx5]),
    strengthsRaw: slice(idx2, [idx3, idx4, idx5]),
    risksRaw:     slice(idx3, [idx4, idx5]),
    signalRaw:    slice(idx4, [idx5]),
  };
};

const bulletsFromText = (raw) => {
  if (!raw) return [];
  return raw
    .split(/\n/)
    .map((l) => l.trim())
    .filter((l) => /^[-•*]/.test(l) || /^\d+[.)]/.test(l))
    .map((l) => stripBoldMarkdown(l.replace(/^[-•*\s]+/, "").replace(/^\d+[.)]\s*/, "")));
};

const parseSignalBlock = (raw) => {
  if (!raw) return { signal: null, reasoning: null };
  const sigMatch = raw.match(/SIGNAL\s*:\s*(BUY|WATCH|HOLD|AVOID)/i);
  const reasonMatch = raw.match(/REASONING\s*:\s*([\s\S]*)/i);
  return {
    signal: sigMatch ? sigMatch[1].toUpperCase() : null,
    reasoning: reasonMatch ? stripBoldMarkdown(reasonMatch[1].trim()) : null,
  };
};

// ---------- Section components ----------
const SectionHeader = ({ tone, icon: Icon, label }) => {
  const tones = {
    teal:    "bg-primary/15 text-primary border-primary/30",
    blue:    "bg-info/15 text-info border-info/30",
    green:   "bg-success/15 text-success border-success/30",
    amber:   "bg-warning/15 text-warning border-warning/30",
    purple:  "bg-[hsl(265_60%_50%/0.15)] text-[hsl(265_60%_50%)] border-[hsl(265_60%_50%/0.3)]",
  };
  return (
    <div className={`flex items-center gap-2 rounded-md border px-3 py-2 ${tones[tone] || tones.teal}`}>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="font-medium text-sm">{label}</span>
    </div>
  );
};

const formatGrowthValue = (key, value) => {
  if (value == null || value === "") return "—";
  if (key.endsWith("_growth") || key.endsWith("_growth_pct"))
    return formatPct(value);
  if (key === "roe" || key === "roe_pct") return formatPct(value);
  if (key === "pe_ratio" || key === "pb_ratio" || key === "debt_to_equity")
    return Number(value).toFixed(2);
  if (key === "market_cap" || key === "market_cap_lkr")
    return formatLKR(value, { compact: true, decimals: 1 });
  if (key === "eps" || key === "dividend_per_share")
    return formatLKR(value);
  return String(value);
};

const FUND_LABELS = {
  eps: "EPS",
  pe_ratio: "P/E Ratio",
  pb_ratio: "P/B Ratio",
  roe: "ROE",
  roe_pct: "ROE",
  debt_to_equity: "Debt / Equity",
  dividend_per_share: "Dividend per Share",
  revenue_growth: "Revenue Growth",
  revenue_growth_pct: "Revenue Growth",
  earnings_growth: "Earnings Growth",
  earnings_growth_pct: "Earnings Growth",
  market_cap: "Market Cap",
  market_cap_lkr: "Market Cap",
};

const isGrowthKey = (k) =>
  k.endsWith("_growth") || k.endsWith("_growth_pct");

// ---------- Main component ----------
const ReportAnalysisModal = ({ state, onClose }) => {
  const [showScrollHint, setShowScrollHint] = useState(true);
  const scrollRef = useRef(null);

  const data = state?.data;
  const open = !!state?.open;

  // ------ Compute sections (use structured fields first, fall back to text) ------
  const parsed = useMemo(() => splitSections(data?.analysis), [data?.analysis]);

  // When scroll hits bottom (within 16px), fade out the hint.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const handler = () => {
      const atBottom = node.scrollHeight - node.scrollTop - node.clientHeight < 24;
      setShowScrollHint(!atBottom);
    };
    handler();
    node.addEventListener("scroll", handler);
    return () => node.removeEventListener("scroll", handler);
  }, [data]);

  if (!state) return null;
  const strengths =
    (data?.analysis_strengths && data.analysis_strengths.length > 0)
      ? data.analysis_strengths.map(stripBoldMarkdown)
      : bulletsFromText(parsed.strengthsRaw);
  const risks =
    (data?.analysis_risks && data.analysis_risks.length > 0)
      ? data.analysis_risks.map(stripBoldMarkdown)
      : bulletsFromText(parsed.risksRaw);
  const signal =
    data?.analysis_signal
    || parseSignalBlock(parsed.signalRaw).signal;
  const reasoning =
    data?.analysis_signal_reasoning
    || parseSignalBlock(parsed.signalRaw).reasoning;

  // Narrative — strip the leading "PART 1" header line if present
  const narrativeBody = (parsed.narrative || data?.analysis || "")
    .replace(/^[\s\S]*?(?:NARRATIVE SUMMARY)\s*\n+/i, "")
    .replace(/^(?:#{1,3}\s*)?PART\s*1[^\n]*\n+/i, "")
    .trim();

  // ------ Key metrics for the chip strip ------
  const fund = data?.analysis_extracted_data || {};
  const keyMetrics = [
    fund.eps != null && { label: "EPS", value: formatLKR(fund.eps) },
    (fund.pe_ratio != null) && { label: "PE", value: `${Number(fund.pe_ratio).toFixed(2)}x` },
    fund.dividend_per_share != null && { label: "DPS", value: formatLKR(fund.dividend_per_share) },
    fund.revenue_growth_pct != null && {
      label: "Rev",
      value: formatPct(fund.revenue_growth_pct),
      tone: fund.revenue_growth_pct >= 0 ? "good" : "bad",
    },
    fund.earnings_growth_pct != null && {
      label: "Earnings",
      value: formatPct(fund.earnings_growth_pct),
      tone: fund.earnings_growth_pct >= 0 ? "good" : "bad",
    },
  ].filter(Boolean);

  // ------ Fundamentals table rows ------
  const fundEntries = Object.entries(fund || {}).filter(([_, v]) => v != null && v !== "");

  const signalTone =
    signal === "BUY"   ? "bg-success/15 text-success border-success/40"
    : signal === "WATCH" ? "bg-warning/15 text-warning border-warning/40"
    : signal === "HOLD"  ? "bg-destructive/15 text-destructive border-destructive/40"
    : signal === "AVOID" ? "bg-destructive/15 text-destructive border-destructive/40"
    : "bg-muted text-muted-foreground border-border";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="p-0 gap-0 overflow-hidden flex flex-col"
        style={{ width: "min(90vw, 900px)", height: "min(90vh, 800px)", maxWidth: "min(90vw, 900px)", maxHeight: "min(90vh, 800px)" }}
        data-testid="report-analysis-dialog"
        showCloseButton={false}
      >
        {/* ------ FIXED HEADER ------ */}
        <div className="border-b bg-card/95 backdrop-blur px-5 py-3 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-display text-base">
                <FileText className="h-4 w-4 text-primary shrink-0" />
                <span className="truncate">{data?.title || "Report analysis"}</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                {data?.report_type && (
                  <Badge variant="outline" className="capitalize text-[10px] px-1.5 py-0">
                    {data.report_type}
                  </Badge>
                )}
                {data?.report_period && <span>· {data.report_period}</span>}
                {data?.analysed_at && <span>· analysed {formatDateTime(data.analysed_at)}</span>}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="shrink-0 -mr-1"
              aria-label="Close"
              data-testid="report-analysis-close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Key-metrics chip strip */}
          {keyMetrics.length > 0 && (
            <>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {keyMetrics.map((m, i) => (
                <Badge
                  key={i}
                  variant="outline"
                  className={`text-[11px] gap-1 px-2 py-0.5 ${
                    m.tone === "good"
                      ? "border-success/40 text-success"
                      : m.tone === "bad"
                      ? "border-destructive/40 text-destructive"
                      : ""
                  }`}
                >
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {m.label}:
                  </span>
                  <span className="font-mono tabular-nums font-medium">
                    {m.value}
                  </span>
                </Badge>
              ))}
              {data?.analysis_extracted_fields?.length > 0 && (
                <Badge
                  variant="outline"
                  className="text-[10px] gap-1 px-1.5 py-0 text-muted-foreground"
                  title={`Fields applied to fundamentals: ${data.analysis_extracted_fields.join(", ")}`}
                >
                  +{data.analysis_extracted_fields.length} fields applied
                </Badge>
              )}
            </div>
            <VisionOcrWarning className="mt-2.5" testId="report-analysis-vision-warning" />
            </>
          )}
        </div>

        {/* ------ SCROLLABLE BODY ------ */}
        <div className="relative flex-1 min-h-0">
          <div
            ref={scrollRef}
            className="absolute inset-0 overflow-y-scroll px-5 py-4 space-y-4"
            data-testid="report-analysis-scroll"
          >
            {state.loading ? (
              <div className="py-14 text-center text-sm text-muted-foreground">
                <Loader2 className="mx-auto h-5 w-5 animate-spin mb-2" />
                Loading Claude's analysis…
              </div>
            ) : !data ? null : (
              <>
                {/* Part 1: Narrative */}
                {narrativeBody && (
                  <section className="space-y-2" data-testid="section-narrative">
                    <SectionHeader tone="teal" icon={FileText} label="Narrative Summary" />
                    <div className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">
                      {stripBoldMarkdown(narrativeBody)}
                    </div>
                  </section>
                )}

                {/* Part 2: Extracted Fundamentals */}
                {fundEntries.length > 0 && (
                  <section className="space-y-2" data-testid="section-fundamentals">
                    <SectionHeader tone="blue" icon={ListChecks} label="Extracted Fundamentals" />
                    <div className="overflow-hidden rounded-md border">
                      <table className="w-full text-sm">
                        <tbody>
                          {fundEntries.map(([k, v]) => {
                            const positive = isGrowthKey(k) && Number(v) > 0;
                            const negative = isGrowthKey(k) && Number(v) < 0;
                            return (
                              <tr key={k} className="border-b last:border-0">
                                <td className="px-3 py-2 text-muted-foreground">
                                  {FUND_LABELS[k] || k}
                                </td>
                                <td
                                  className={`px-3 py-2 text-right font-mono tabular-nums ${
                                    positive ? "text-success font-medium"
                                    : negative ? "text-destructive font-medium"
                                    : ""
                                  }`}
                                >
                                  {formatGrowthValue(k, v)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {data.analysis_extraction_notes && (
                      <p className="text-[11px] text-muted-foreground italic">
                        {data.analysis_extraction_notes}
                      </p>
                    )}
                  </section>
                )}

                {/* Part 3: Strengths */}
                {strengths.length > 0 && (
                  <section className="space-y-2" data-testid="section-strengths">
                    <SectionHeader tone="green" icon={CheckCircle2} label="Key Strengths" />
                    <ul className="space-y-1.5 text-sm">
                      {strengths.map((s, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="h-3.5 w-3.5 mt-1 text-success shrink-0" />
                          <span className="leading-relaxed">{s}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Part 4: Risks */}
                {risks.length > 0 && (
                  <section className="space-y-2" data-testid="section-risks">
                    <SectionHeader tone="amber" icon={AlertTriangle} label="Key Risks" />
                    <ul className="space-y-1.5 text-sm">
                      {risks.map((s, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <AlertTriangle className="h-3.5 w-3.5 mt-1 text-warning shrink-0" />
                          <span className="leading-relaxed">{s}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Part 5: Signal */}
                {(signal || reasoning) && (
                  <section className="space-y-2" data-testid="section-signal">
                    <SectionHeader tone="purple" icon={Target} label="Investment Signal" />
                    <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                      {signal && (
                        <Badge
                          variant="outline"
                          className={`text-sm font-semibold px-3 py-1 ${signalTone}`}
                          data-testid="report-signal-badge"
                        >
                          {signal}
                        </Badge>
                      )}
                      {reasoning && (
                        <p className="text-sm leading-relaxed text-foreground/90">
                          {reasoning}
                        </p>
                      )}
                    </div>
                  </section>
                )}

                {/* Source PDF link */}
                <div className="border-t pt-3 flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-[11px] text-muted-foreground">
                    {data.file_size_bytes
                      ? `Original PDF — ${(data.file_size_bytes / (1024 * 1024)).toFixed(1)} MB`
                      : "Original PDF"}
                  </span>
                  <Button asChild size="sm" variant="outline" className="gap-1.5">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/file/${data.report_id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="report-analysis-download"
                    >
                      <Download className="h-3.5 w-3.5" /> Download PDF
                    </a>
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Scroll-hint gradient */}
          {showScrollHint && !state.loading && (
            <div
              className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 flex items-end justify-center"
              aria-hidden
            >
              <div className="absolute inset-0 bg-gradient-to-t from-card via-card/70 to-transparent" />
              <div className="relative flex items-center gap-1 text-[11px] text-muted-foreground pb-1.5">
                <ChevronDown className="h-3 w-3 animate-bounce" />
                <span>Scroll to read more</span>
                <ChevronDown className="h-3 w-3 animate-bounce" />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportAnalysisModal;
